import { useEffect, useRef, useState } from "react";
import { createRequestsSocket } from "../lib/socket";
import { useAuth } from "../state/auth.jsx";

const EVT = {
  INIT: [],                         // if you add WS snapshot later  ["requests.init"]
  NEW: ["request.created"],         // from backend
  UPDATED: ["request.updated"],     // from backend
};

export function useRequestsSocket({ listParams = {} } = {}) {
  const { accessToken, api } = useAuth();
  const [requests, setRequests] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const socketRef = useRef(null);

  const refetch = async () => {
    try {
      setLoading(true);
      const res = await api.get("/requests", { params: listParams }); //  Active or Archived w/ filters
      const list = res.data?.requests || res.data || [];
      setRequests(list);
      const c = list.length
        ? list.map(r => r.created_on).filter(Boolean).sort().slice(-1)[0]
        : new Date().toISOString();
      setCursor(c);
      setError(null);
    } catch (e) {
      setError(e?.message || "Failed to fetch requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    refetch();                              // REST snapshot

    const socket = createRequestsSocket(accessToken, cursor);
    socketRef.current = socket;

    EVT.INIT.forEach(evt => socket.on(evt, (payload) => {
      const list = Array.isArray(payload) ? payload :
        Array.isArray(payload?.items) ? payload.items : [];
      setRequests(list);
      if (payload?.cursor) setCursor(payload.cursor);
      setLoading(false);
    }));

    EVT.NEW.forEach(evt => socket.on(evt, (req) => {
      if (!req || !req.id) return;
      const isArchived = (req.status === 'archived') || !!req.is_archived;
      const filter = (listParams?.status === 'archived') ? 'archived' : 'active';
      const matches = filter === 'archived' ? isArchived : !isArchived;
      if (!matches) return; // don't insert into the wrong view
      setRequests(prev => {
        const exists = prev.some(r => r.id === req.id);
        return exists ? prev.map(r => (r.id === req.id ? { ...r, ...req } : r)) : [req, ...prev];
      });
      if (req.created_on) setCursor(prev => (prev && prev > req.created_on ? prev : req.created_on));
    }));

    EVT.UPDATED.forEach(evt => socket.on(evt, (req) => {
      if (!req || !req.id) return;
      const isArchived = (req.status === 'archived') || !!req.is_archived;
      const filter = (listParams?.status === 'archived') ? 'archived' : 'active';
      const matches = filter === 'archived' ? isArchived : !isArchived;
      setRequests(prev => {
        const exists = prev.some(r => r.id === req.id);
        if (!matches) {
          // if it no longer belongs in this view, drop it
          return exists ? prev.filter(r => r.id !== req.id) : prev;
        }
        // otherwise upsert
        return exists ? prev.map(r => (r.id === req.id ? { ...r, ...req } : r)) : [req, ...prev];
      });
    }));

    socket.connect();
    return () => { try { socket.disconnect(); } catch { } socketRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, JSON.stringify(listParams)]);

  // Writes → REST (optimistic), server will broadcast UPDATED
  const updateRequest = async (id, patch) => {
    setRequests(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
    try { await api.patch(`/requests/${id}`, patch); }
    catch (e) { setError(e?.message || "Update failed"); refetch(); }
  };

  const addNote = async (id, note) => {
    try { await api.post(`/requests/${id}/notes`, { notes: note }); }
    catch (e) { setError(e?.message || "Add note failed"); }
  };

  const archiveRequest = async (id) => {
    try { await api.post(`/requests/${id}/archive`, { archived: true }); }
    catch (e) { setError(e?.message || "Archive failed"); }
  };

  const unarchiveRequest = async (id) => {
    try { await api.post(`/requests/${id}/archive`, { archived: false }); }
    catch (e) { setError(e?.message || "Unarchive failed"); }
  };
  return { requests, loading, error, updateRequest, addNote, archiveRequest, unarchiveRequest, refetch, socket: socketRef.current };
}

